// ====================================================================
// CLIENT FOLLOW-UP CRM - REPORTS & ANALYTICS VIEW (ADMIN ONLY)
// ====================================================================

import { getInteractions, getClients, getUsers } from '../store.js';
import { escapeHtml, formatDate, showToast } from '../ui.js';

let filteredReportData = [];

export async function renderReportsView(container) {
  const interactions = await getInteractions();
  const clients = await getClients();
  const users = await getUsers();

  container.innerHTML = `
    <div class="page-header">
      <div>
        <h1 class="page-title">Reports & Export Center</h1>
        <p class="page-subtitle">Generate custom client interaction reports and export data to CSV / Excel</p>
      </div>
      <div style="display:flex; gap:0.75rem;">
        <button id="btn-export-csv" class="btn btn-secondary">
          <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
          Export CSV
        </button>
        <button id="btn-export-excel" class="btn btn-success">
          <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
          Export Excel
        </button>
      </div>
    </div>

    <!-- Filter Bar -->
    <div class="card" style="margin-bottom: 1.5rem;">
      <div class="form-row">
        <div class="form-group" style="margin-bottom:0;">
          <label class="form-label">Category</label>
          <select id="rep-filter-cat" class="form-select">
            <option value="">All Categories</option>
            <option value="Hot">Hot</option>
            <option value="Warm">Warm</option>
            <option value="Cold">Cold</option>
            <option value="Closed">Closed</option>
          </select>
        </div>

        <div class="form-group" style="margin-bottom:0;">
          <label class="form-label">Status</label>
          <select id="rep-filter-status" class="form-select">
            <option value="">All Statuses</option>
            <option value="New Lead">New Lead</option>
            <option value="Called">Called</option>
            <option value="Interested">Interested</option>
            <option value="Follow-up">Follow-up</option>
            <option value="Meeting Scheduled">Meeting Scheduled</option>
            <option value="Quotation Sent">Quotation Sent</option>
            <option value="Negotiation">Negotiation</option>
            <option value="Won">Won</option>
            <option value="Lost">Lost</option>
            <option value="Closed">Closed</option>
          </select>
        </div>

        <div class="form-group" style="margin-bottom:0;">
          <label class="form-label">Owner</label>
          <select id="rep-filter-owner" class="form-select">
            <option value="">All Owners</option>
            ${users.map(u => `<option value="${u.id}">${escapeHtml(u.name)}</option>`).join('')}
          </select>
        </div>
      </div>
    </div>

    <!-- Report Table Container -->
    <div class="card">
      <div class="card-header">
        <h3 class="card-title">Interaction Records</h3>
        <span id="rep-count-badge" class="badge badge-upcoming">${interactions.length} Records</span>
      </div>
      <div class="table-responsive">
        <table class="table" id="report-table">
          <thead>
            <tr>
              <th>Client Name</th>
              <th>Company</th>
              <th>Mobile</th>
              <th>Interaction Date</th>
              <th>Status</th>
              <th>Next Follow-up</th>
              <th>Notes</th>
              <th>Logged By</th>
            </tr>
          </thead>
          <tbody id="report-tbody">
            <!-- Dynamic rows -->
          </tbody>
        </table>
      </div>
    </div>
  `;

  const applyReportFilters = () => {
    const cat = document.getElementById('rep-filter-cat').value;
    const status = document.getElementById('rep-filter-status').value;
    const owner = document.getElementById('rep-filter-owner').value;

    filteredReportData = interactions.filter(i => {
      const client = clients.find(c => c.id === i.client_id);
      const matchCat = !cat || (client && client.category === cat);
      const matchStatus = !status || i.status === status;
      const matchOwner = !owner || i.created_by === owner;
      return matchCat && matchStatus && matchOwner;
    });

    document.getElementById('rep-count-badge').textContent = `${filteredReportData.length} Records`;

    const tbody = document.getElementById('report-tbody');
    if (filteredReportData.length === 0) {
      tbody.innerHTML = `<tr><td colspan="8" style="text-align:center; color:var(--text-muted); padding:2rem;">No matching interaction records found.</td></tr>`;
      return;
    }

    tbody.innerHTML = filteredReportData.map(i => {
      const client = clients.find(c => c.id === i.client_id);
      return `
        <tr>
          <td><strong>${escapeHtml(client?.client_name || 'Unknown')}</strong></td>
          <td>${escapeHtml(client?.company || '-')}</td>
          <td>${escapeHtml(client?.mobile_number || '-')}</td>
          <td>${formatDate(i.interaction_date)}</td>
          <td><span class="badge badge-warm">${escapeHtml(i.status)}</span></td>
          <td>${formatDate(i.next_followup_date)}</td>
          <td style="max-width:220px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">${escapeHtml(i.notes || '-')}</td>
          <td>${escapeHtml(i.creator?.name || 'Staff')}</td>
        </tr>
      `;
    }).join('');
  };

  applyReportFilters();

  document.getElementById('rep-filter-cat').addEventListener('change', applyReportFilters);
  document.getElementById('rep-filter-status').addEventListener('change', applyReportFilters);
  document.getElementById('rep-filter-owner').addEventListener('change', applyReportFilters);

  // CSV Exporter
  document.getElementById('btn-export-csv').addEventListener('click', () => {
    exportToCSV(filteredReportData, clients);
  });

  // Excel Exporter
  document.getElementById('btn-export-excel').addEventListener('click', () => {
    exportToExcel(filteredReportData, clients);
  });
}

function exportToCSV(data, clients) {
  if (!data || data.length === 0) {
    showToast('No data to export', 'error');
    return;
  }

  const headers = ['Client Name', 'Company', 'Mobile Number', 'Interaction Date', 'Status', 'Next Followup Date', 'Notes', 'Logged By'];
  const rows = data.map(i => {
    const client = clients.find(c => c.id === i.client_id);
    return [
      `"${(client?.client_name || '').replace(/"/g, '""')}"`,
      `"${(client?.company || '').replace(/"/g, '""')}"`,
      `"${(client?.mobile_number || '').replace(/"/g, '""')}"`,
      `"${formatDate(i.interaction_date)}"`,
      `"${(i.status || '').replace(/"/g, '""')}"`,
      `"${formatDate(i.next_followup_date)}"`,
      `"${(i.notes || '').replace(/"/g, '""')}"`,
      `"${(i.creator?.name || 'Staff').replace(/"/g, '""')}"`
    ].join(',');
  });

  const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows].join('\n');
  const encodedUri = encodeURI(csvContent);
  const link = document.createElement('a');
  link.setAttribute('href', encodedUri);
  link.setAttribute('download', `CRM_Interactions_Report_${new Date().toISOString().split('T')[0]}.csv`);
  document.body.appendChild(link);
  link.click();
  link.remove();

  showToast('CSV report downloaded successfully', 'success');
}

function exportToExcel(data, clients) {
  if (!data || data.length === 0) {
    showToast('No data to export', 'error');
    return;
  }

  let tableHtml = `<table border="1"><tr><th>Client Name</th><th>Company</th><th>Mobile Number</th><th>Interaction Date</th><th>Status</th><th>Next Followup Date</th><th>Notes</th><th>Logged By</th></tr>`;
  data.forEach(i => {
    const client = clients.find(c => c.id === i.client_id);
    tableHtml += `<tr>
      <td>${escapeHtml(client?.client_name || '')}</td>
      <td>${escapeHtml(client?.company || '')}</td>
      <td>${escapeHtml(client?.mobile_number || '')}</td>
      <td>${formatDate(i.interaction_date)}</td>
      <td>${escapeHtml(i.status)}</td>
      <td>${formatDate(i.next_followup_date)}</td>
      <td>${escapeHtml(i.notes || '')}</td>
      <td>${escapeHtml(i.creator?.name || 'Staff')}</td>
    </tr>`;
  });
  tableHtml += `</table>`;

  const blob = new Blob([tableHtml], { type: 'application/vnd.ms-excel' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `CRM_Interactions_Report_${new Date().toISOString().split('T')[0]}.xls`;
  document.body.appendChild(a);
  a.click();
  a.remove();

  showToast('Excel report downloaded successfully', 'success');
}
